import CONSTANT, { CACHE } from '../utils/constants';
import { FLOW, PAYMODE } from '../../../common/src/constant';
import { ifSuccessOrThrowError, getApi } from '../utils/api-response-utils';
import { getKey, equalsIgnoreCase, money } from '../utils/common';

const _PCF_KEY_DELIMITER = '@';

/* -------------------------------------------------------------------------- */
/*                              DEFAULT FLOW PCF                              */
/* -------------------------------------------------------------------------- */

function _fetchPcfDetailsForDefault(cache, payMode, txnAmount) {
  return getApi(cache)
    .fetchPcfDetails(payMode, txnAmount)
    .then(response => ifSuccessOrThrowError(response, callBackResponse => callBackResponse));
}

/* -------------------------------------------------------------------------- */
/*                              PCF API Evaluation                            */
/* -------------------------------------------------------------------------- */

function _getPcfApi() {
  // To be later used when deferred is in place.
  // const { flow } = cache.getData(CACHE.MERCHANT_CONFIG);

  // return equalsIgnoreCase(flow, FLOW.DEFERRED) ?
  //     _fetchPcfDetailsForDeferred :
  //     _fetchPcfDetailsForDefault;

  return _fetchPcfDetailsForDefault;
}

/* -------------------------------------------------------------------------- */
/*                              PCF methods                            */
/* -------------------------------------------------------------------------- */

function _getPcfKeyPayMethodObj(payMethodObj) {
  const prefixedInstId = payMethodObj.instId ? _PCF_KEY_DELIMITER + payMethodObj.instId : '';

  return payMethodObj.payMethod + prefixedInstId;
}
function _getPayMethodObj(payMode, channelCode) {
  const payMethodObj = {};

  if (equalsIgnoreCase(payMode.type, 'sc')) {
    payMethodObj.payMethod = getKey(payMode, 'cardDetails.cardType', '').toUpperCase();
    payMethodObj.instId = getKey(payMode, 'issuingBank');
  } else {
    payMethodObj.payMethod = payMode.paymentMode.toUpperCase();

    if (channelCode) {
      payMethodObj.instId = channelCode;
    }
  }

  return payMethodObj;
}

/* -------------------------------------------------------------------------- */
/*                              PCF methods                            */
/* -------------------------------------------------------------------------- */

function _fetchPcf(cache, payMethods, txnAmount) {
  return _getPcfApi()(cache, payMethods, txnAmount).then(response => {
    const consultDetails = getKey(response, 'consultDetails', {});
    const convenienceCharges = { ...cache.getData(CACHE.CONVENIENCE_CHARGES, {}) };
    const isSinglePayMethods = payMethods.length === 1;

    Object.keys(consultDetails).forEach(payMode => {
      const payModeKey = isSinglePayMethods ? _getPcfKeyPayMethodObj(payMethods[0]) : payMode;
      convenienceCharges[payModeKey] = consultDetails[payMode];
    });

    cache.setData(CACHE.CONVENIENCE_CHARGES, convenienceCharges);

    return convenienceCharges;
  });
}

export function fetchPcfForPayMode(cache, payMode, channelCode, txnAmount = 0) {
  if (Object.prototype.hasOwnProperty.call(payMode, 'isWalletOnly')) {
    payMode = { ...payMode, paymentMode: CONSTANT.WALLET };
  }

  const payMethodObj = _getPayMethodObj(payMode, channelCode);
  const payModeKey = _getPcfKeyPayMethodObj(payMethodObj);
  const savedConvenienceCharges = cache.getData(CACHE.CONVENIENCE_CHARGES, {});
  const cachedChargesForPayMode = savedConvenienceCharges[payModeKey];

  if (cachedChargesForPayMode) {
    return Promise.resolve(cachedChargesForPayMode);
  }

  return _fetchPcf(cache, [payMethodObj], txnAmount).then(
    (convenienceCharges = {}) => convenienceCharges[payModeKey]
  );
}

export function fetchPcfForAllPayModes(cache, txnAmount = 0) {
  const paymentOptions = cache.getData('paymentOptions');
  const paymentModes = [...getKey(paymentOptions, 'payData.paymentModes', [])];

  // Manually append wallet pay mode since wallet is not available in pay modes cache.
  if (getKey(paymentOptions, 'wallet')) {
    paymentModes.push({ paymentMode: CONSTANT.WALLET });
  }

  const payMethods = [];
  paymentModes.forEach(mode => {
    if (equalsIgnoreCase(mode.paymentMode, PAYMODE.LOGIN)) {
      return;
    }

    // Need to append CC or DC as only one card type is included
    // in pay mode during parsing of pay modes.
    if (equalsIgnoreCase(mode.paymentMode, CONSTANT.CC)) {
      payMethods.push(
        _getPayMethodObj({
          paymentMode: CONSTANT.DC
        })
      );
    } else if (equalsIgnoreCase(mode.paymentMode, CONSTANT.DC)) {
      payMethods.push(
        _getPayMethodObj({
          paymentMode: CONSTANT.CC
        })
      );
    }

    payMethods.push(_getPayMethodObj(mode));
  });

  if (payMethods.length) {
    return _fetchPcf(cache, payMethods, txnAmount).then(charges => {
      return Object.keys(charges).reduce((acc, chargeKey) => {
        if (chargeKey.indexOf(_PCF_KEY_DELIMITER) === -1) {
          acc[chargeKey] = charges[chargeKey];
        }
        return acc;
      }, {});
    });
  }

  return Promise.resolve({});
}

export function isPcfEnabled(cache) {
  // Currently PCF is not supported for DEFERRED
  const { flow } = cache.getData(CACHE.MERCHANT_CONFIG);

  if (equalsIgnoreCase(flow, FLOW.DEFERRED)) {
    return false;
  }

  const paymentOptions = cache.getData(CACHE.PAYMENT_OPTIONS, {});
  return getKey(paymentOptions, 'pcfEnabled', false);
}

export function updatedPcfAmountAndReturn(cache, pcf) {
  const txnAmount = cache.getData(CONSTANT.TXN_AMOUNT);
  const fallBackAmount = cache.getData(CONSTANT.WITHOUT_PCF_TXN_AMOUNT, txnAmount);
  let pcfFeeAmount = 0;
  let newTransactionAmount = fallBackAmount;

  cache.setData(CONSTANT.WITHOUT_PCF_TXN_AMOUNT, fallBackAmount);

  if (pcf) {
    const { taxAmount, feeAmount } = pcf;
    pcfFeeAmount = Number(taxAmount.value) + Number(feeAmount.value);
    newTransactionAmount = money(Number(fallBackAmount) + pcfFeeAmount);
  }

  cache.setData(CONSTANT.TXN_AMOUNT, newTransactionAmount);
  return pcfFeeAmount;
}

export function updatePcfInCache(cache, payMode, channelCode, pcf) {
  const convenienceCharges = { ...cache.getData(CACHE.CONVENIENCE_CHARGES, {}) };
  const payMethodObj = _getPayMethodObj(payMode, channelCode);
  const payModeKey = _getPcfKeyPayMethodObj(payMethodObj);
  convenienceCharges[payModeKey] = pcf;
  cache.setData(CACHE.CONVENIENCE_CHARGES, convenienceCharges);
}



// WEBPACK FOOTER //
// ./controllers/pcf.js